import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Compass, Map, Tent, Flame, ThermometerSun, Mountain, ShieldAlert, Crosshair } from 'lucide-react';

const SurvivalSkills: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Apex Survival School";
    
    return (
        <div className="min-h-screen bg-[#2b2e25] font-mono text-[#d4d8c6] selection:bg-[#ff4500] selection:text-white border-[1px] border-[#42463b] m-2 relative overflow-hidden">
            
            {/* Topographic Lines Overlay (CSS) */}
            <div className="fixed inset-0 pointer-events-none opacity-10 z-0">
                 <svg width="100%" height="100%">
                    <defs>
                        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#d4d8c6" strokeWidth="0.5"/>
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#grid)" />
                 </svg>
            </div>

            {/* HUD Corners */}
            <div className="fixed top-6 left-6 w-8 h-8 border-t-2 border-l-2 border-[#ff4500] z-50"></div>
            <div className="fixed top-6 right-6 w-8 h-8 border-t-2 border-r-2 border-[#ff4500] z-50"></div>
            <div className="fixed bottom-6 left-6 w-8 h-8 border-b-2 border-l-2 border-[#ff4500] z-50"></div>
            <div className="fixed bottom-6 right-6 w-8 h-8 border-b-2 border-r-2 border-[#ff4500] z-50"></div>

            {/* Navbar / Status Bar */}
            <nav className="relative z-40 bg-[#1e201a] border-b border-[#42463b] px-6 py-4 flex justify-between items-center text-xs uppercase tracking-widest">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-[#ff4500]">
                        <Compass size={18} />
                        <span className="font-bold">{schoolName}</span>
                    </div>
                    <div className="hidden md:flex gap-4 text-[#8a8f80]">
                        <span>Lat: 45.20N</span>
                        <span>Lon: 122.30W</span>
                        <span className="text-[#a3cf02]">Status: Active</span>
                    </div>
                </div>

                <div className="flex gap-8">
                    <a href="#" className="hover:text-[#ff4500] transition-colors">[ Courses ]</a>
                    <a href="#" className="hover:text-[#ff4500] transition-colors">[ Handbook ]</a>
                    <a href="#" className="hover:text-[#ff4500] transition-colors">[ Gear ]</a>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-0 border-b border-[#42463b]">
                <div className="lg:col-span-8 p-12 lg:p-24 flex flex-col justify-center bg-[#2b2e25]">
                    <div className="inline-block border border-[#ff4500] text-[#ff4500] px-2 py-1 text-[10px] font-bold uppercase tracking-widest mb-6 w-fit">
                        Warning: Rigorous Training
                    </div>
                    
                    <h1 className="text-4xl md:text-7xl font-bold uppercase mb-8 leading-none tracking-tighter">
                        Survive <br/>
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#a3cf02] to-[#6d8a01]">The Wild.</span>
                    </h1>
                    
                    <p className="text-sm md:text-base text-[#8a8f80] max-w-lg mb-12 leading-relaxed">
                        // PROTOCOL: Learn essential wilderness survival techniques. Shelter building, water purification, navigation, and psychological resilience.
                    </p>
                    
                    <div className="flex gap-4">
                        <Button className="bg-[#ff4500] hover:bg-[#cc3700] text-black font-bold uppercase tracking-widest text-xs h-12 px-8 rounded-sm hover:skew-x-[-10deg] transition-transform">
                            Enroll Now
                        </Button>
                        <Button variant="outline" className="border-[#8a8f80] text-[#8a8f80] hover:border-[#d4d8c6] hover:text-[#d4d8c6] hover:bg-transparent font-bold uppercase tracking-widest text-xs h-12 px-8 rounded-sm hover:skew-x-[-10deg] transition-transform">
                            View Syllabus
                        </Button>
                    </div>
                </div>

                {/* Tactical Sidebar / Image */}
                <div className="lg:col-span-4 border-t lg:border-t-0 lg:border-l border-[#42463b] relative min-h-[400px]">
                    <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1533610738683-1e967d605178?q=80&w=1964&auto=format&fit=crop')] bg-cover bg-center grayscale contrast-125 opacity-40 mix-blend-overlay"></div>
                    <div className="absolute inset-0 bg-[#1e201a]/80"></div>
                    
                    <div className="relative z-10 p-8 h-full flex flex-col justify-between">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="border border-[#42463b] p-4 bg-[#1e201a]">
                                <ThermometerSun size={20} className="text-[#ff4500] mb-2" />
                                <div className="text-[10px] text-[#8a8f80] uppercase">Temp</div>
                                <div className="text-lg font-bold">12°C</div>
                            </div>
                            <div className="border border-[#42463b] p-4 bg-[#1e201a]">
                                <Mountain size={20} className="text-[#a3cf02] mb-2" />
                                <div className="text-[10px] text-[#8a8f80] uppercase">Elev</div>
                                <div className="text-lg font-bold">2400m</div>
                            </div>
                        </div>

                        <div className="font-mono text-[10px] text-[#8a8f80]">
                            Sector 4 Training Ground <br/>
                            Visibility: Good <br/>
                            Wind: NW 12km/h
                        </div>
                    </div>
                </div>
            </header>

            {/* Modules / Grid */}
            <section className="relative z-10 p-12 lg:p-24">
                <div className="flex items-center gap-4 mb-12 border-b border-[#42463b] pb-4">
                    <Crosshair className="text-[#ff4500]" />
                    <h2 className="text-2xl font-bold uppercase tracking-widest">Training Modules</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { icon: Tent, title: "Shelter Craft", code: "MOD-01" },
                        { icon: Flame, title: "Fire Start", code: "MOD-02" },
                        { icon: Map, title: "Land Nav", code: "MOD-03" },
                        { icon: ShieldAlert, title: "First Aid", code: "MOD-04" },
                    ].map((item, i) => (
                        <div key={i} className="bg-[#1e201a] border border-[#42463b] p-8 hover:border-[#a3cf02] transition-colors group cursor-pointer relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-2 text-[10px] bg-[#42463b] text-[#d4d8c6] group-hover:bg-[#a3cf02] group-hover:text-black transition-colors">
                                {item.code}
                            </div>
                            <item.icon size={32} className="text-[#8a8f80] mb-6 group-hover:text-[#d4d8c6] transition-colors" />
                            <h3 className="text-lg font-bold uppercase mb-2 text-[#d4d8c6]">{item.title}</h3>
                            <div className="w-full bg-[#2b2e25] h-1 mt-4">
                                <div className="bg-[#ff4500] h-full w-0 group-hover:w-full transition-all duration-500"></div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

             {/* Footer */}
            <footer className="relative z-10 border-t border-[#42463b] bg-[#1a1c16] py-12 px-6 font-mono text-xs text-[#5c6055] uppercase tracking-widest">
                 <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
                     <div>
                         &copy; 2026 {schoolName}. All systems operational.
                     </div>
                     <div className="flex gap-8">
                         <a href="#" className="hover:text-[#d4d8c6]">Waiver</a>
                         <a href="#" className="hover:text-[#d4d8c6]">Medical</a>
                         <a href="#" className="hover:text-[#d4d8c6]">Contact</a>
                     </div>
                 </div>
            </footer>
        </div>
    );
};

export default SurvivalSkills;
